import React, { useState, useCallback, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiLogIn } from 'react-icons/fi';
import AuthDropdown from '../auth/AuthDropdown';
import { useUIStore } from '../../stores/uiStore';
import logo from '../../assets/images/logo.png';

const WebsiteNavbar: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authView, setAuthView] = useState<'login' | 'register'>('login');

  // Close dropdown if auth modal opens (from page CTAs)
  const activeModal = useUIStore((s) => s.activeModal);
  useEffect(() => {
    if (activeModal === 'auth-login' || activeModal === 'auth-register') {
      setAuthOpen(false);
    }
  }, [activeModal]);
  const location = useLocation();

  const navLinks = [
    { label: 'How to Play', path: '/how-to-play' },
    { label: 'FAQ', path: '/faq' },
  ];

  const isActive = (path: string) => location.pathname === path;

  const openAuth = (view: 'login' | 'register') => {
    setAuthView(view);
    setAuthOpen(true);
    setMobileMenuOpen(false);
  };

  const closeAuth = useCallback(() => setAuthOpen(false), []);

  return (
    <nav className="fixed top-0 w-full bg-[#0a0f0d]/95 backdrop-blur-lg border-b border-white/5 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img src={logo} alt="CricClash" className="w-9 h-9 rounded-lg object-cover" />
            <span className="text-xl font-bold text-white">CricClash</span>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-[#00FF85]'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Actions — relative container for dropdown */}
          <div className="hidden md:flex items-center gap-3 relative">
            <button
              onClick={() => openAuth('login')}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-300 hover:text-white border border-white/20 hover:border-white/40 rounded-lg transition-all"
            >
              <FiLogIn className="w-4 h-4" />
              Login
            </button>
            <button
              onClick={() => openAuth('register')}
              className="inline-flex items-center gap-2 px-5 py-2 bg-[#00FF85] text-black rounded-lg text-sm font-bold hover:bg-[#00E077] transition-all"
            >
              Create Account
            </button>

            {/* Auth dropdown tray */}
            <AuthDropdown isOpen={authOpen} onClose={closeAuth} initialView={authView} />
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-gray-400 hover:text-white"
          >
            {mobileMenuOpen ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10">
            <div className="space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-2.5 rounded-lg text-sm font-medium ${
                    isActive(link.path)
                      ? 'text-[#00FF85] bg-[#00FF85]/10'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <button
                onClick={() => openAuth('login')}
                className="flex items-center justify-center gap-2 w-full px-4 py-2.5 border border-white/20 text-gray-300 rounded-lg text-sm font-medium mt-4 hover:text-white hover:border-white/40"
              >
                <FiLogIn className="w-4 h-4" />
                Login
              </button>
              <button
                onClick={() => openAuth('register')}
                className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-[#00FF85] text-black rounded-lg text-sm font-bold hover:bg-[#00E077]"
              >
                Create Account
              </button>
            </div>

            {/* Mobile auth dropdown renders inline */}
            {authOpen && (
              <div className="mt-4 border-t border-white/10 pt-4">
                <AuthDropdown isOpen={authOpen} onClose={closeAuth} initialView={authView} />
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default WebsiteNavbar;
